/* eslint-disable jsx-a11y/anchor-is-valid */
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';
import Spinner from '~/components/common/spinner.component';
import { ROLE_SLUGS, ROUTES } from '~/constants';
import useCompanyContext from '~/hooks/company-scope.hook';
import { useGetAuditOverviewQuery } from '~/schema';
import capitalize from '~/utils/string';
import HasRole from '~/components/auth/has-role.component';
import Info from '~/components/common/info.component';
import composeUrl from '~/utils/route';
import SiteAuditListItem from '~/components/audit/site-audit-list-item.component';
import Modal from '~/components/common/modal.component';
import { useState } from 'react';
import CreateSite from '../sites/create-site.container';

const AuditsContainer = () => {
  const { t } = useTranslation();
  const router = useRouter();
  const [isCreatingSite, setIsCreatingSite] = useState(false);
  const { company } = useCompanyContext();

  const {
    data: auditOverview,
    loading: overviewLoading,
    error: auditsError,
    refetch,
  } = useGetAuditOverviewQuery({
    variables: {
      companyId: company?.id,
    },
    fetchPolicy: 'no-cache',
  });

  const siteAudits = auditOverview?.auditOverview?.sites || [];

  return (
    <>
      <Modal
        className="w-6/12"
        isOpen={isCreatingSite}
        onClose={() => setIsCreatingSite(false)}
      >
        <CreateSite
          onSiteCreated={() => {
            refetch();
            setIsCreatingSite(false);
          }}
          onCancel={() => setIsCreatingSite(false)}
        />
      </Modal>
      <div className="m-6 pb-1">
        <div className="flex flex-row justify-between align-center">
          <div className="flex flex-col">
            <h3>{capitalize(t('Actieve audits'))}</h3>
            <p>
              Beheer actieve audits per site. Elke site kan maar één actieve
              audit hebben.
            </p>
          </div>
          <HasRole
            requiredRole={[
              ROLE_SLUGS.ROOT,
              ROLE_SLUGS.TENANT_ADMINISTRATOR,
              ROLE_SLUGS.TENANT_USER,
              ROLE_SLUGS.COMPANY_ADMINISTRATOR,
              ROLE_SLUGS.COMPANY_USER,
            ]}
          >
            <div>
              <button
                type="button"
                className="c-button--outline flex flex-row align-center"
                onClick={() => {
                  setIsCreatingSite(true);
                }}
              >
                {capitalize(t('nieuwe site'))}
              </button>
            </div>
          </HasRole>
        </div>
        {overviewLoading && (
          <div className="h-screen flex items-center justify-center">
            <Spinner />
          </div>
        )}
        {auditsError && (
          <span className="c-error mt-1">
            {t(
              'Er liep iets fout bij het laden van de pagina. Probeer het later opnieuw',
            )}
          </span>
        )}
        {!overviewLoading && (
          <>
            <div className="my-4 grid grid-cols-2 gap-4">
              {siteAudits.length === 0 && (
                <Info>{capitalize(t('nog geen audits voor deze klant'))}</Info>
              )}
              {siteAudits.map(({ isOutdated, site, audit }) => (
                <SiteAuditListItem
                  key={site.id}
                  isOutdated={isOutdated}
                  site={site}
                  audit={audit}
                  onCreate={({ auditId }: { auditId?: string }) => {
                    router.push(
                      composeUrl(ROUTES.CREATE_AUDIT, {
                        siteId: site.id,
                        auditId,
                      }),
                    );
                  }}
                  onOpen={() =>
                    router.push(composeUrl(ROUTES.AUDIT, { id: audit.id }))
                  }
                />
              ))}
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default AuditsContainer;
